import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/react';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormikProps } from 'formik/dist/types';
import { Formik } from 'formik';
import { GenericMessages } from 'src/intl/GenericMessages';
import UpdateJobDetailsForm from './UpdateJobDetailsForm';
import { JobMessages } from 'src/intl/JobMessages';
import { useDispatch, useSelector } from 'react-redux';
import { selectLogTags } from '../../../redux/logTags/selector';
import { AutocompleteSelectItem } from '../../reusable-components/AutocompleteSelect';
import { fetchLogTagsByOrgId } from '../../../redux/logTags/thunk/logTags';
import { useSocket } from '../../../utils/socket';
import { SocketContextSentinel } from '../../../utils/socket/context/sentinelSocketContext';
import type { ThunkDispatch } from 'redux-thunk';
import type { AppState } from '../../../redux/types';
import type { AnyAction } from 'redux';
import { ToastType, useToast } from '../../reusable-components/useToast';

export interface UpdateJobDetailsModalProps {
  jobId?: string;
  orgId?: string;
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal.
   */
  onClose: () => void;
  /**
   * The existing job detail values being updated.
   */
  existingJobDetailValues: Partial<JobDetailsFormFields>;
  originalFileName: string;
  tagId: string;
}

export interface JobDetailsFormFields {
  siteName: string;
  description: string;
  logTagItem: AutocompleteSelectItem;
  heatExchangerModelNumber: string;
  timezone: string;
}

export const UpdateJobDetailsModal: React.FC<UpdateJobDetailsModalProps> = ({
  jobId,
  orgId,
  onClose,
  isOpen,
  existingJobDetailValues,
  originalFileName,
  tagId,
}) => {
  const { formatMessage } = useIntl();

  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const { request: logsRequest, update: updateLog } = useSocket(SocketContextSentinel, 'logs');
  const [present] = useToast();
  const raiseToast = useCallback(
    (type: ToastType, header: string) => {
      present({ message: header }, type).then(); // then() consumes Promise, eliminating lint warning
    },
    [present],
  );

  const onUpdate = useMemo(() => {
    return async (formValues: JobDetailsFormFields) => {
      try {
        // If the log tag is new, then send a request that creates a log-tags
        // entity and then adds the id of that entity to the log and updates
        // any other fields as needed.
        if (formValues.logTagItem.isNewItem) {
          const updateWithNewLogTagRequest = {
            newLogTag: formValues.logTagItem.text,
            _id: jobId,
            update: {
              $set: {
                description: formValues.description,
                siteName: formValues.siteName,
                heatExchangerModelNumber: formValues.heatExchangerModelNumber,
                deviceTimezone: formValues.timezone,
              },
            },
          };
          await logsRequest('logs update-with-new-log-tag', updateWithNewLogTagRequest);

          // Refresh log tags since there is now a new one.
          if (orgId) {
            dispatch(fetchLogTagsByOrgId(orgId));
          }
        } else {
          //   Otherwise, just update the log as needed.
          const request: Record<string, any> = {};
          request._id = jobId;
          const logTagIds = formValues.logTagItem.id ? [formValues.logTagItem.id] : [];
          request.update = {
            $set: {
              description: formValues.description,
              logTagIds,
              siteName: formValues.siteName,
              heatExchangerModelNumber: formValues.heatExchangerModelNumber,
              deviceTimezone: formValues.timezone,
            },
          };
          await updateLog(request);
        }
      } catch (e) {
        console.error('Could not update job log:', e);
        raiseToast('danger', formatMessage(JobMessages.updateDetailsFailure));
        onClose();
        throw e;
      }
      raiseToast('success', formatMessage(JobMessages.updateDetailsSuccess));
      onClose();
    };
  }, [dispatch, formatMessage, jobId, logsRequest, onClose, raiseToast, updateLog, orgId]);

  const handleClose = React.useCallback(
    (formikProps: FormikProps<JobDetailsFormFields>) => {
      // Form can be closed if description already exists for the job.
      if (!!existingJobDetailValues?.description) {
        formikProps.resetForm();
        onClose();
      }
    },
    [existingJobDetailValues?.description, onClose],
  );

  const validate = React.useCallback(
    (values: JobDetailsFormFields) => {
      return !!values.description ? {} : { description: formatMessage(GenericMessages.fieldRequiredLabel) };
    },
    [formatMessage],
  );

  const tryFetchLogTagsState = useSelector(selectLogTags);
  const selectedLogTagItem = useMemo(() => {
    const selectedLogTag = tryFetchLogTagsState?.logTags?.find(logTag => logTag._id === tagId);
    return selectedLogTag
      ? new AutocompleteSelectItem(selectedLogTag._id, selectedLogTag.tag)
      : new AutocompleteSelectItem('', '');
  }, [tagId, tryFetchLogTagsState.logTags]);

  return (
    <Formik
      initialValues={{
        siteName: existingJobDetailValues?.siteName || '',
        description: existingJobDetailValues?.description || '',
        logTagItem: selectedLogTagItem,
        heatExchangerModelNumber: existingJobDetailValues?.heatExchangerModelNumber || '',
        timezone: existingJobDetailValues?.timezone || 'UTC',
      }}
      onSubmit={onUpdate}
      enableReinitialize={true}
      validate={validate}
      validateOnChange={true}
    >
      {(formikProps: FormikProps<JobDetailsFormFields>) => (
        <IonModal
          isOpen={isOpen}
          onDidDismiss={() => handleClose(formikProps)}
          canDismiss={!!existingJobDetailValues?.description || formikProps.isValid}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                <FormattedMessage {...JobMessages.updateJobDetails} />
              </IonTitle>
              <IonButtons slot="start">
                <IonButton
                  onClick={() => handleClose(formikProps)}
                  disabled={!existingJobDetailValues?.description}
                  fill="clear"
                  color="dark"
                >
                  <FormattedMessage {...GenericMessages.closeButtonPlaceholder} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent>
            <form onSubmit={formikProps.handleSubmit}>
              <UpdateJobDetailsForm formikProps={formikProps} originalFilename={originalFileName} />
              <div className="job-details-footer">
                <IonButton
                  disabled={!formikProps.isValid}
                  className="job-details-upload-button"
                  expand="block"
                  size="large"
                  type="submit"
                >
                  <FormattedMessage {...GenericMessages.submitButtonLabel} />
                </IonButton>
              </div>
            </form>
          </IonContent>
        </IonModal>
      )}
    </Formik>
  );
};
export default UpdateJobDetailsModal;
